import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import BaseHeader from 'components/Header/BaseHeader';
import BaseFooter from 'components/BaseFooter';
import Promobar from 'components/Promobar/Promobar.vue';

import useAccount from 'composables/useAccount';
import useIsMobile from 'common/composables/useIsMobile.js';

import CartQuery from 'assets/graphql/queries/Cart.graphql';
import CategoriesQuery from 'assets/graphql/queries/Categories.graphql';
import CurrentCampaignQuery from 'assets/graphql/queries/CurrentCampaign.graphql';
import UserWishlistQuery from 'assets/graphql/queries/UserWishlistItems.graphql';
import SiteConfigQuery from 'assets/graphql/queries/SiteConfig.graphql';
import BasicHeader from 'components/BasicHeader/BasicHeader.vue';

@Component({
  apollo: {
    campaign: {
      query: CurrentCampaignQuery,
      variables: {
        filters: '{"show_banner": true}',
      },
    },
    cart: {
      query: CartQuery,
    },
    categories: {
      query: CategoriesQuery,
      update: data => data?.categories,
    },
    wishlistItems: {
      query: UserWishlistQuery,
      update: data => data.wishitems,
    },
    siteConfig: {
      query: SiteConfigQuery,
    },
  },
  components: {
    BaseFooter,
    BaseHeader,
    Promobar,
    BasicHeader,
  },
  setup() {
    const { getUserData, getUserLogged, onUserStatus, onUserData, loading } =
      useAccount();
    const { isMobile } = useIsMobile();

    return {
      auth_user: getUserData,
      logged_user: getUserLogged,
      onUserStatus,
      onUserData,
      loading,
      isMobile,
    };
  },
})
export default class BaseViewComponent extends Vue {
  categories = [];
  boxes = [];
  baseHeaderProps = {};
  menuOn = false;
  showOverlay = false;
  showHeader = true;
  showBasicHeader = false;
  showFooter = true;
  remindersModalView = false;
  showReminderOnboarding = false;
  savedYWindowScroll = null;
  showBasicHeader = false;

  mounted() {
    this.onUserStatus();
    this.onUserData();
  }

  menuOpen() {
    this.menuOn = !this.menuOn;
  }

  updated() {
    if (this.$route.name !== 'box-catalog' && this.$route.name !== 'box') {
      this.scrollToTop();
    }
    this.$root.$on('updateHeaderAndFooter', ({ showHeader, showFooter }) => {
      this.showHeader = showHeader;
      this.showFooter = showFooter;
    });
  }

  @Watch('$route')
  routeUpdated() {
    this.$refs.header?.routeUpdated();
  }

  @Watch('showOverlay')
  onShowOverlay(val) {
    // Now we manage the ecommerce block scroll from here based in showOverlay
    if (val && this.isMobile) {
      // Save the last window scrollY because if we fix the position and hidde the overflow it loses the scroll position
      this.savedYWindowScroll = window.scrollY;

      // when isMobile and showOverlay === true, we block page scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
    }

    if (!val && this.isMobile) {
      // when isMobile === true and showOverlay === false we enable the scroll again
      document.body.style.overflow = 'auto';
      document.body.style.position = 'static';

      // If routes names matches we return to the last window scrollY saved
      if (
        (this.$route.name === 'box-catalog' || this.$route.name === 'box') &&
        this.savedYWindowScroll
      ) {
        window.scrollTo({
          top: this.savedYWindowScroll,
          behavior: 'instant',
        });
      }
    }
  }

  updateShowOverlay(payload) {
    this.showOverlay = payload;
  }

  showCart() {
    this.$refs.header.showCartMenu();
    this.scrollToTop();
  }

  scrollToTop() {
    window.scrollTo(0, 0);
  }

  refetchUser() {
    this.onUserStatus();
  }

  toggleRemindersModalView(status) {
    this.remindersModalView = status;
  }

  toggleOnboarding(status) {
    this.showReminderOnboarding = status;
  }

  get isRetailMode() {
    return this.$route.query?.retail === 'true';
  }

  get showHeaderGetter() {
    const routeName = this.$route.name;
    const excludedRoutes = ['express-login'];

    // If the route is in the excludedRoutes array or is retail mode, return false
    if (excludedRoutes.includes(routeName) || this.isRetailMode) {
      return false;
    }
    // If the route is not in the excludedRoutes array, return the showHeader value
    // This logic is used to give priority to the excludedRoutes
    return this.showHeader;
  }

  get showFooterGetter() {
    const routeName = this.$route.name;
    const excludedRoutes = ['express-login'];

    // If the route is in the excludedRoutes array or is retail mode, return false
    if (excludedRoutes.includes(routeName) || this.isRetailMode) {
      return false;
    }
    // If the route is not in the excludedRoutes array, return the showFooter value
    // This logic is used to give priority to the excludedRoutes
    return this.showFooter;
  }

  /**
   * The promobar should not be rendered inside the following routes.
   */
  get showPromobar() {
    const routeName = this.$route.name;
    const excludedRoutes = [
      'box-catalog',
      'account',
      'accountGifts',
      'accountPurchases',
      'accountCredit',
      'regalendario',
      'express-login',
    ];
    return !excludedRoutes.includes(routeName) && !this.isRetailMode;
  }

  get basicHeaderConfig() {
    const routeName = this.$route.name;
    const configs = {
      'express-login': {
        title: 'Iniciar sesión',
        showBackButton: true,
        backRoute: null, // null means use go(-1)
      },
      products: {
        title: 'Volver',
        showBackButton: true,
        backRoute: null, // null means use go(-1)
      },
      box: {
        title: 'Volver',
        showBackButton: true,
        backRoute: null, // null means use go(-1)
      },
      boxActivity: {
        title: 'Volver',
        showBackButton: true,
        backRoute: null, // null means use go(-1)
      },
      activity: {
        title: 'Volver',
        showBackButton: true,
        backRoute: null, // null means use go(-1)
      },
    };

    return configs[routeName] || null;
  }

  get showBasicHeaderGetter() {
    const routeName = this.$route.name;
    const includedRoutes = ['express-login'];

    // If the route is in the includedRoutes array, return true
    // Or if the route is in retail mode, return true
    return includedRoutes.includes(routeName) || this.isRetailMode;
  }

  get promobarSlides() {
    return (
      this.siteConfig?.promobars
        ?.map(s => ({
          content: s.title ?? '',
          href: s.redirect_url,
        }))
        .filter(s => !!s.content) ?? []
    );
  }
}
